/**
 * ``useDeploymentConfig`` — TanStack Query hook around the unauthenticated
 * ``GET /api/config`` endpoint, the source of the classification-badge
 * marking (REQ-UI-058) **and** the advertised basemap tile descriptors
 * (``map.tile_options[]``; REQ-UI-033 / REQ-UI-091; ADR-016 amended
 * 2026-06-10). The Map seam resolves the rendered descriptor from the
 * advertised ``tileOptions`` + the Profile's selected ``map.tile_server``
 * id (:func:`selectTileDescriptor`) to build its MapLibre basemap source
 * browser-direct or via the retained BFF proxy; the Settings picker reads
 * ``tileOptions`` to populate its dropdown.
 *
 * # Fail-safe (CLAUDE.md §8.1)
 *
 * Returns ``null`` while the fetch is pending and on ANY error (network,
 * non-2xx, shape-validation). ``null`` drives the badge's ``UNVERIFIED``
 * alarm — the badge is therefore never blank and never shows a specific
 * (possibly-too-low) level until the deployment value is confirmed. This
 * is fail-safe, not fail-open.
 *
 * # Mount point
 *
 * The endpoint is **authenticated** (``/api/config`` is deliberately NOT
 * on the BFF SessionMiddleware skip-list — since the 2026-05-29
 * classification-markings refinement it is consumed post-login only; an
 * unauthenticated user redirects straight to Keycloak). At MVP it is
 * consumed by the top-bar :class:`ClassificationBadge` (via
 * :func:`useClassification`), which renders on every authenticated
 * screen, so the marking is always present.
 *
 * # Stale time
 *
 * ``Infinity`` — the deployment config changes only on redeploy (which
 * reloads the page), so there is never a reason to refetch within a
 * session.
 *
 * Cross-references:
 *   - REQ-UI-058 (classification badge); REQ-UI-026 (TanStack Query);
 *     REQ-UI-035 (BFF-only).
 *   - REQ-UI-033 / REQ-UI-034 / REQ-UI-091 (advertised basemap tile
 *     descriptors); ADR-016 amended 2026-06-10.
 *   - chrome/use-classification.ts (the seam that consumes this).
 *   - map/use-tile-descriptor.ts (the Map seam that resolves the rendered
 *     descriptor from ``tileOptions`` + the Profile's ``map.tile_server``).
 *   - profiles/profile-editor.tsx (the Settings basemap picker; REQ-UI-091).
 *   - packages/meridian_bff/src/meridian_bff/_config_routes.py.
 */
import { useQuery } from "@tanstack/react-query";
import { apiFetch, ApiError } from "@/lib/api-client";
import type { components } from "@/lib/api-types.gen";
import { readPublishedPortRange, type PublishedPortRange } from "@/lib/listen-port-range";
import type { ResolvedClassification } from "@/chrome/classification-badge";

/** Wire response for ``GET /api/config`` (codegen). */
export type ConfigResponse = components["schemas"]["ConfigResponse"];

/**
 * One advertised basemap tile source (codegen mirror of the BFF's
 * ``TileSourceDescriptor``; ADR-016 amended 2026-06-10).
 *
 * Self-contained so the UI builds the MapLibre source directly: an
 * **absolute** ``url_template`` is fetched browser-direct (REQ-UI-033 /
 * REQ-UI-034 — the operator's browser reaches a per-network tile
 * server the air-gapped backend cannot); the relative
 * ``/api/tiles/...`` form routes through the retained BFF proxy.
 *
 * Cross-references:
 *   - REQ-UI-033 / REQ-UI-034 / REQ-UI-091; ADR-016 amended 2026-06-10.
 */
export type TileSourceDescriptor = components["schemas"]["TileSourceDescriptor"];

/**
 * Fail-safe default basemap descriptor — OpenFreeMap (vector, English
 * labels), browser-direct, keyless (the deployment-A default that
 * ``GET /api/config`` advertises with ``default: true``).
 *
 * Used ONLY when a **resolved** config advertises no ``map.tile_options``
 * (an older BFF) — the deployment has spoken and named nothing, so the
 * baked A default is the best available guess. It is **not** used while
 * the config is pending or errored: since #1002 the Map renders *no*
 * basemap in those states. Building the first paint on this descriptor
 * used to be the fail-safe (#644 aligned it with the BFF's baked default
 * to avoid a first-paint swap), but on a same-origin PMTiles / org-hosted
 * deployment the host is CSP-blocked, the source never loads, MapLibre
 * never fires ``load``, and the real descriptor could never be applied —
 * a permanently dead map for any browser that mounted before
 * ``/api/config`` was applied. Show-partial-over-blank still holds: the
 * markers render over the theme background while the descriptor is
 * unresolved (CLAUDE.md §8.1).
 *
 * Cross-references:
 *   - REQ-UI-033 / REQ-UI-034; ADR-016 amended 2026-06-10 (+ 2026-07-15);
 *     CLAUDE.md §8.1.
 */
export const DEFAULT_TILE_DESCRIPTOR: TileSourceDescriptor = {
  // Aligned byte-for-byte with the BFF's A-default descriptor
  // (``packages/meridian_bff/src/meridian_bff/_config.py`` —
  // ``_OPENFREEMAP_TILE_URL_TEMPLATE`` + the ``tile_*`` field defaults) so
  // the older-BFF fallback and a modern deployment's advertised
  // ``default: true`` coincide. Baked default flipped NRL → OpenFreeMap per
  // ADR-016 amended 2026-07-15 / #644. Not used for the first paint since
  // #1002 (see the docstring above).
  id: "openfreemap",
  label: "Street map (English)",
  type: "vector",
  url_template: "https://tiles.openfreemap.org/planet",
  layer: "",
  format: "pbf",
  dark_layer: null,
  attribution: "© OpenStreetMap contributors",
  scheme: "xyz",
  max_zoom: 20,
  requires_key: false,
  key_param: null,
  default: true,
};

/** The resolved per-deployment UI config the chrome consumes. */
export interface ResolvedDeploymentConfig {
  /**
   * The deployment's declared marking (style + caption), or ``null`` when it
   * declared none — which the badge renders as ``⚠ UNVERIFIED`` (ADR-042 D-3).
   */
  readonly classification: ResolvedClassification | null;
  /** Application title — the top-bar wordmark (per-deployment codename). */
  readonly title: string;
  /**
   * The advertised basemap tile sources (``map.tile_options[]``;
   * REQ-UI-033 / REQ-UI-091). The **raw** list — the single descriptor to
   * render is resolved downstream by :func:`selectTileDescriptor` against
   * the Profile's selected ``map.tile_server`` id (the Map seam
   * :file:`map/use-tile-descriptor.ts`) and the picker reads it to populate
   * the Settings dropdown (:file:`profiles/profile-editor.tsx`). Empty when
   * the config advertises none (older BFF) — the Map seam then falls back
   * to :const:`DEFAULT_TILE_DESCRIPTOR` (a resolved, option-less config is
   * the only state that still uses it; #1002).
   */
  readonly tileOptions: readonly TileSourceDescriptor[];
}

/**
 * Pick the descriptor the map should render from the advertised options,
 * honouring the operator's Profile selection (REQ-UI-091).
 *
 * Selection order (fail-safe; CLAUDE.md §8.1 — the map must always end up
 * with a basemap):
 *   1. the advertised descriptor whose ``id`` equals ``selectedId`` (the
 *      Profile's ``map.tile_server``);
 *   2. the advertised ``default: true`` descriptor — used when ``selectedId``
 *      is unset/blank ("deployment default") **or** names an option this
 *      deployment no longer advertises (storyboard U-profiles-and-settings
 *      edge-case table: a stale Profile id falls back to the deployment
 *      default, not to nothing);
 *   3. the first advertised, when none carries the ``default`` flag;
 *   4. :const:`DEFAULT_TILE_DESCRIPTOR` when the list is empty / ``undefined``
 *      (an older BFF that advertises no ``map.tile_options``).
 *
 * @param options - The advertised ``map.tile_options`` (may be empty
 *     / ``undefined`` on an older BFF).
 * @param selectedId - The Profile's selected ``map.tile_server`` option id
 *     (``null`` / ``undefined`` / blank ⇒ deployment default).
 * @returns The descriptor to build the MapLibre basemap from.
 *
 * Cross-references:
 *   - REQ-UI-091 (Profile-selected basemap); ADR-016 amended 2026-06-10.
 *   - storyboard flows/U-profiles-and-settings (stale-id → default fallback).
 */
export function selectTileDescriptor(
  options: readonly TileSourceDescriptor[] | undefined,
  selectedId: string | null | undefined,
): TileSourceDescriptor {
  if (options === undefined || options.length === 0) {
    return DEFAULT_TILE_DESCRIPTOR;
  }
  const wanted = selectedId?.trim();
  if (wanted !== undefined && wanted !== "") {
    const match = options.find((d) => d.id === wanted);
    if (match !== undefined) {
      return match;
    }
    // A stale / unknown id falls through to the deployment default below
    // rather than blanking the basemap.
  }
  return options.find((d) => d.default) ?? options[0];
}

/** Query key — exported so a future consumer can invalidate if needed. */
export const DEPLOYMENT_CONFIG_QUERY_KEY = ["deployment-config"] as const;

/**
 * Fetch the deployment config and surface the resolved values.
 *
 * The endpoint is authenticated, so this only resolves inside the
 * authenticated app (the chrome mounts post-login).
 *
 * @returns The resolved ``{ classification, title }`` on success, or
 *     ``null`` while pending / on any error (the fail-safe state — the
 *     banner shows UNVERIFIED, the wordmark its default).
 */
export function useDeploymentConfig(): ResolvedDeploymentConfig | null {
  const query = useDeploymentConfigQuery();

  if (query.data === undefined) {
    // Pending or errored → fail-safe (banner UNVERIFIED, default wordmark).
    return null;
  }
  return {
    // Null when the deployment declared no marking — the badge's fail-safe
    // UNVERIFIED state, not an error (ADR-042 D-3).
    classification:
      query.data.classification.style != null && query.data.classification.text != null
        ? {
            style: query.data.classification.style,
            text: query.data.classification.text,
          }
        : null,
    title: query.data.branding.title,
    // ``map`` is parsed leniently (see :func:`isConfigResponse`): an
    // older BFF may omit it, so optional-chain into ``tile_options`` and
    // surface an empty list. A *resolved* but option-less config is the
    // one case the Map seam still serves from :const:`DEFAULT_TILE_DESCRIPTOR`
    // (#1002 — pending / errored render no basemap instead).
    tileOptions: query.data.map?.tile_options ?? [],
  };
}

/** The query's lifecycle, for consumers that must tell "not yet" from "never". */
export type DeploymentConfigStatus = "pending" | "error" | "success";

/**
 * The ``GET /api/config`` query's status — a sibling reader over the SAME
 * query as :func:`useDeploymentConfig` (same key ⇒ one fetch, consistent
 * views), for the one consumer that needs the distinction that hook
 * deliberately collapses: the Map seam (:file:`map/use-tile-descriptor.ts`).
 * While **pending** it renders no basemap and waits; on **error** it renders
 * no basemap and says so once. Building the first paint on a network guess
 * instead used to wedge the map for good on deployments whose CSP forbids
 * that host (#1002).
 *
 * @returns ``"pending"`` until the first response, ``"error"`` once the
 *     query has given up (a 401/403 is never retried; anything else twice),
 *     ``"success"`` when a valid payload is in hand.
 */
export function useDeploymentConfigStatus(): DeploymentConfigStatus {
  const query = useDeploymentConfigQuery();
  if (query.isError) {
    return "error";
  }
  if (query.data === undefined) {
    return "pending";
  }
  return "success";
}

/** The one TanStack Query behind both readers above (module-private). */
function useDeploymentConfigQuery() {
  return useQuery<ConfigResponse, ApiError>({
    queryKey: DEPLOYMENT_CONFIG_QUERY_KEY,
    queryFn: async ({ signal }) => {
      const raw = await apiFetch<unknown>("/api/config", { signal });
      if (!isConfigResponse(raw)) {
        // Boundary-validation failure — surface loudly per CLAUDE.md
        // §8.1; the banner falls back to UNVERIFIED.
        console.error(
          "[meridian/chrome/use-deployment-config] /api/config failed shape validation",
          raw,
        );
        throw new ApiError(500, raw, "config response did not match the expected shape");
      }
      return raw;
    },
    // Static per deployment — never refetch within a session.
    staleTime: Number.POSITIVE_INFINITY,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

/**
 * Thin reader exposing just the advertised ``map.tile_options[]`` from the
 * shared :func:`useDeploymentConfig` query — the Profile editor's basemap
 * picker consumes this to populate its dropdown (REQ-UI-091), and the Map
 * seam (:file:`map/use-tile-descriptor.ts`) resolves the rendered descriptor
 * from it.
 *
 * Returns an empty list while the config is pending / on any error / when the
 * deployment advertises none (older BFF). An empty list reads as "deployment
 * default only" at the picker (it shows the single default-label row). The
 * Map seam distinguishes the three via :func:`useDeploymentConfigStatus`
 * (no basemap while pending / errored; :const:`DEFAULT_TILE_DESCRIPTOR`
 * only for a resolved-but-empty list; #1002).
 *
 * @returns The advertised tile descriptors, or ``[]`` until they resolve.
 *
 * Cross-references:
 *   - REQ-UI-091 (Profile-selected basemap); ADR-016 amended 2026-06-10.
 *   - profiles/profile-editor.tsx (the picker); map/use-tile-descriptor.ts.
 */
export function useTileOptions(): readonly TileSourceDescriptor[] {
  return useDeploymentConfig()?.tileOptions ?? [];
}

/**
 * Narrow an ``unknown`` BFF payload to :type:`ConfigResponse`.
 *
 * Validates only the badge-critical fields (``classification`` +
 * ``branding.title``). ``map`` is parsed **leniently** — the BFF
 * declares it required, but a pre-increment-1 deployment may omit it,
 * so the narrowing does not gate on it; the descriptor resolution
 * (:func:`selectTileDescriptor`, in the Map seam) falls back to the baked
 * OpenFreeMap default (:const:`DEFAULT_TILE_DESCRIPTOR`) when a *resolved*
 * config carries no ``map`` / ``tile_options``. This keeps the banner
 * fail-safe without coupling its validity to the map config (CLAUDE.md
 * §8.1); pending / errored configs render no basemap at all (#1002).
 */
function isConfigResponse(value: unknown): value is ConfigResponse {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  const c = obj.classification;
  if (typeof c !== "object" || c === null) return false;
  const cls = c as Record<string, unknown>;
  const b = obj.branding;
  if (typeof b !== "object" || b === null) return false;
  const branding = b as Record<string, unknown>;
  // A marking is valid only when BOTH halves are present and the style is one
  // the badge can render. Either half missing means the deployment declared no
  // marking, which is a resolved-but-UNVERIFIED state rather than a malformed
  // response — so the predicate accepts it and the hook maps it to null
  // (ADR-042 D-3; CLAUDE.md §8.1).
  const styleOk =
    cls.style === "green" ||
    cls.style === "amber" ||
    cls.style === "red" ||
    cls.style === "neutral";
  const markingWellFormed =
    (styleOk && typeof cls.text === "string") ||
    ((cls.style === null || cls.style === undefined) &&
      (cls.text === null || cls.text === undefined));
  return markingWellFormed && typeof branding.title === "string";
}

// ---- The live window (ADR-041 D-7; REQ-UI-181) ------------------------

/**
 * The live window the UI assumes until ``GET /api/config`` resolves — the
 * BFF's own default (``DEFAULT_LIVE_WINDOW_SECONDS`` in
 * ``meridian_bff._config``), so a client that has not yet read the config and
 * the routes it calls agree. Never blank, never zero (the classification
 * badge's fail-safe posture).
 */
export const DEFAULT_LIVE_WINDOW_SECONDS = 600;

/**
 * The deployment's live window in **milliseconds** — how far back the map,
 * the Tracks panel and the Detections panel count as "recent" (REQ-UI-181).
 *
 * One value, read once per session from ``live.window_seconds`` on
 * ``GET /api/config`` (REQ-QUERY-056), fed to the detections buffer's prune
 * floor, the object-track store's prune floor, the recent-list polls, the
 * viewport backfill and the glyph / line age fade — so the two panels can
 * never disagree on it. Falls back to :const:`DEFAULT_LIVE_WINDOW_SECONDS`
 * while the config is pending, errored, or from an older BFF that does not
 * advertise it.
 */
export function useLiveWindowMs(): number {
  const query = useDeploymentConfigQuery();
  const seconds = query.data?.live?.window_seconds;
  return (
    (typeof seconds === "number" && seconds > 0 ? seconds : DEFAULT_LIVE_WINDOW_SECONDS) * 1000
  );
}

// ---- The published listener port ranges (REQ-UI-198) -------------------

/**
 * The two listener port ranges the deployment publishes to the host — what a
 * provider ``listen`` / peer ``pull`` connection's port must sit inside
 * (REQ-DEPLOY-054), read from ``listen_port_ranges`` on ``GET /api/config``
 * (REQ-PROVIDER-057 / REQ-PEER-114's advertisement) so the connection editors
 * can show the range and refuse an outside port before the BFF does
 * (REQ-UI-198).
 *
 * A sibling reader over the SAME query as :func:`useDeploymentConfig` (same
 * key ⇒ one fetch). ``null`` while the config is pending or errored, and when
 * the BFF predates the ranges or advertises a malformed one — the editors then
 * refuse nothing and say the range is not advertised, leaving the BFF the
 * authority on save (CLAUDE.md §8.1: no false refusal from a missing fact).
 * Each range is narrowed independently, so one bad half does not blind the
 * other.
 *
 * @returns Both ranges, or ``null`` until a valid advertisement resolves.
 */
export function useListenPortRanges(): PublishedListenPortRanges | null {
  const query = useDeploymentConfigQuery();
  const advertised = (query.data as { listen_port_ranges?: unknown } | undefined)
    ?.listen_port_ranges;
  if (typeof advertised !== "object" || advertised === null) return null;
  const { provider, peer } = advertised as { provider?: unknown; peer?: unknown };
  return { provider: readPublishedPortRange(provider), peer: readPublishedPortRange(peer) };
}

/** The advertised ranges, each ``null`` when that half is absent or malformed. */
export interface PublishedListenPortRanges {
  readonly provider: PublishedPortRange | null;
  readonly peer: PublishedPortRange | null;
}
